import { MigrationPlan } from "../../api/migrationsApi";

/**
 * Decisions behind the "Migrate from Wildlife Insights" button on the project page (AKL-812).
 *
 * Kept as pure functions so they can be unit-tested without rendering
 * `ManageProjectDetails`, which fires a dozen requests on mount.
 */

/**
 * The project permissions a target-project migration needs — all of them.
 *
 * Mirrors `REQUIRED_PROJECT_PERMISSIONS` in the API's `migration_service.py`: the server
 * refuses a migration into an existing project unless the user holds every one of these, so
 * the button must not be offered to anyone the server would turn away. If that list changes,
 * this one moves with it.
 */
export const MIGRATION_TARGET_PERMISSIONS = [
  "EDIT_PROJECT_DETAILS",
  "CREATE_DEPLOYMENTS_LOCATIONS",
  "EDIT_IDENTIFICATIONS",
] as const;

export type MigrationEntryInput = {
  /** True on the Edit Project page, false on the New Project form. */
  existingProject: boolean;
  isAdmin: boolean;
  /**
   * How many organisations the New Project form offers in its Organization picker
   * (`/organizations/projectoptions`). The same list feeds the migration modal in create
   * mode, so the button and the picker can never disagree about who may create a project.
   */
  organizationOptionCount: number;
  /** The user's permissions on the project being edited. Ignored in create mode. */
  perms: { name: string }[];
};

/**
 * Whether the project page may offer the Wildlife Insights migration.
 *
 * Create mode (New Project form): an admin, or anyone with at least one organisation they
 * may create a project in. Target mode (Edit Project page): only a user holding all of
 * {@link MIGRATION_TARGET_PERMISSIONS}, which is the second-pass flow — attaching
 * identifications to a project whose photos have since arrived.
 * @param input the page mode and what is known about the user
 * @returns true when the button should render
 */
export function canOpenWildlifeInsightsMigration(
  input: MigrationEntryInput,
): boolean {
  if (!input.existingProject) {
    return input.isAdmin || input.organizationOptionCount > 0;
  }
  const held = new Set(input.perms.map((permission) => permission.name));
  return MIGRATION_TARGET_PERMISSIONS.every((name) => held.has(name));
}

/**
 * The akili projects an applied migration created or matched, for deciding where to send
 * the user afterwards.
 *
 * `skipped` sections and sections with no akili id are ignored — there is nothing to land
 * on. One id means "go to that project"; several means "go to the projects list"; none
 * means "stay where you are".
 * @param result the applied job's `result` (or `plan`) report
 * @returns the project ids, in bundle order, with no duplicates
 */
export function migrationLandingProjectIds(
  result: MigrationPlan | null | undefined,
): number[] {
  if (!result) return [];
  const ids: number[] = [];
  for (const section of result.projects) {
    if (
      (section.action === "create" || section.action === "match") &&
      section.project_id !== null &&
      !ids.includes(section.project_id)
    ) {
      ids.push(section.project_id);
    }
  }
  return ids;
}
